import { Component, computed, effect, inject, model, OnDestroy, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { DialogModule } from "primeng/dialog";
import { ButtonModule } from "primeng/button";
import { TooltipModule } from "primeng/tooltip";
import { LoggerService, ParsedLogEntry } from "../../../../services/logger.service";
import { prettyPrintEmbeddedJson } from "../../../../utils/formatting-helper";
import { LogTableComponent } from "../../../log-table/log-table.component";

const AUTO_REFRESH_INTERVAL_MS = 15000;
const REFRESH_TICK_INTERVAL_MS = 250;

@Component({
  selector: "app-log-viewer-modal",
  standalone: true,
  imports: [CommonModule, DialogModule, ButtonModule, TooltipModule, LogTableComponent],
  templateUrl: "./log-viewer-modal.component.html",
  styleUrls: ["./log-viewer-modal.component.scss"]
})
export class LogViewerModalComponent implements OnDestroy {
  private readonly logger = inject(LoggerService);
  private readonly rawLogs = signal<ParsedLogEntry[]>([]);
  private readonly nextRefreshAt = signal<number>(0);
  private readonly tickNow = signal<number>(Date.now());
  private autoRefreshTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private tickIntervalId: ReturnType<typeof setInterval> | null = null;

  visible = model<boolean>(false);

  parsedLogs = computed<ParsedLogEntry[]>(() => [...this.rawLogs()]
    .reverse()
    .map(log => ({ ...log, message: prettyPrintEmbeddedJson(log.message) })));

  secondsUntilNextRefresh = computed(() => {
    const remainingMs = Math.max(0, this.nextRefreshAt() - this.tickNow());
    return Math.ceil(remainingMs / 1000);
  });

  // Depletes clockwise from a full circle down to nothing as the next auto-refresh approaches.
  refreshPieBackground = computed(() => {
    const remainingMs = Math.max(0, this.nextRefreshAt() - this.tickNow());
    const elapsedFraction = 1 - (remainingMs / AUTO_REFRESH_INTERVAL_MS);
    const degrees = elapsedFraction * 360;
    return `conic-gradient(var(--gray-200, #e5e7eb) ${degrees}deg, var(--gray-400, #9ca3af) ${degrees}deg 360deg)`;
  });

  constructor() {
    effect(() => {
      if (this.visible()) {
        this.refreshLogs();
      } else {
        this.clearRefreshTimers();
      }
    });
  }

  ngOnDestroy(): void {
    this.clearRefreshTimers();
  }

  refreshLogs(): void {
    this.rawLogs.set(this.logger.getParsedLogs());
    // Reschedule from now so a manual refresh doesn't leave a stale auto-refresh queued right behind it.
    this.scheduleNextRefresh();
  }

  private scheduleNextRefresh(): void {
    this.clearRefreshTimers();
    this.nextRefreshAt.set(Date.now() + AUTO_REFRESH_INTERVAL_MS);
    this.autoRefreshTimeoutId = setTimeout(() => this.refreshLogs(), AUTO_REFRESH_INTERVAL_MS);
    this.tickIntervalId = setInterval(() => this.tickNow.set(Date.now()), REFRESH_TICK_INTERVAL_MS);
  }

  private clearRefreshTimers(): void {
    if (this.autoRefreshTimeoutId) {
      clearTimeout(this.autoRefreshTimeoutId);
      this.autoRefreshTimeoutId = null;
    }
    if (this.tickIntervalId) {
      clearInterval(this.tickIntervalId);
      this.tickIntervalId = null;
    }
  }
}
